import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import ButtonLink from './ButtonLink';
import { useTranslation } from '../lib/hooks';
import { ThemeProps } from '@lib/theme';

const NavWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  overflow-x: scroll;
  margin-bottom: 2rem;

  & > *:not(:first-child) {
    margin-left: ${(p) => p.theme.spacing(0.25)};
  }

  ::-webkit-scrollbar {
    display: none;
  }
`;

interface OnPageNavigationProps {
  extension: string;
  options: Array<string>;
}

const Dot = styled.div<ThemeProps & { highlight: boolean }>(
  ({ theme, highlight }) => `
  background: ${highlight ? theme.colors.primaryColor : theme.colors.faded};
  height: 0.5rem;
  width: 0.5rem;
  border-radius: 50%;
  margin: 1rem 0.5rem;
`,
);

const Dots = styled.div.withConfig({
  shouldForwardProp: (p) => !['display'].includes(p),
})<ThemeProps & { display: boolean }>(
  ({ display }) => `
  display: ${display ? 'flex' : 'none'};
  width: 100%;
  margin-top: -2rem;
  justify-content: center;
  margin-bottom: 1rem;
  `,
);

const OnPageNavigation = ({ extension, options }: OnPageNavigationProps) => {
  const { pathname } = useRouter();
  const t = useTranslation();
  const [displayDots, setDisplayDots] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isCurrent = (segment: string) =>
    pathname.split(`${extension}/`)[1] === segment;

  useEffect(() => {
    if (wrapperRef.current) {
      const extendsBeyondScreen =
        wrapperRef.current.scrollWidth - wrapperRef.current.clientWidth > 0;
      setDisplayDots(extendsBeyondScreen);
    }
  }, [wrapperRef]);

  return (
    <>
      <NavWrapper ref={wrapperRef}>
        {options.map((o) => {
          return (
            <ButtonLink
              key={o}
              {...(isCurrent(o)
                ? {
                    btn: 'alt',
                    'aria-current': 'page',
                    href: undefined,
                  }
                : { btn: 'secondary' })}
              href={`/${extension}/${o.toLowerCase()}`}
            >
              {t(`${extension}.navigation_option.${o}`)}
            </ButtonLink>
          );
        })}
      </NavWrapper>
      <Dots display={displayDots}>
        {options.map((o) => (
          <Dot key={o} highlight={isCurrent(o)} />
        ))}
      </Dots>
    </>
  );
};

export default OnPageNavigation;
